import { createContext, useContext, useEffect, useState, useCallback, type ReactNode } from "react";
import { supabase } from "@/integrations/supabase/client";
import type { User, Session } from "@supabase/supabase-js";

type AppRole = "admin" | "agent" | "client" | "co_buyer" | "mls_admin";

const VIEW_AS_KEY = "privy:view-as-role";
export const VALID_ROLES = ["admin", "agent", "client", "co_buyer", "mls_admin"] as const;

interface AgentStatus {
  isApproved: boolean;
  onboardingComplete: boolean;
}

interface AuthContextType {
  user: User | null;
  session: Session | null;
  /** The effective role (respects viewAsRole override) */
  role: AppRole | null;
  /** The real database role, unaffected by preview mode */
  actualRole: AppRole | null;
  agentStatus: AgentStatus | null;
  mustUpdatePassword: boolean;
  loading: boolean;
  /** Currently previewing as this role (null = not previewing) */
  viewAsRole: AppRole | null;
  /** Set a role override for UI preview (admin only) */
  setViewAsRole: (role: AppRole | null) => void;
  signOut: () => Promise<void>;
  refreshRole: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
};

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [actualRole, setActualRole] = useState<AppRole | null>(null);
  const [agentStatus, setAgentStatus] = useState<AgentStatus | null>(null);
  const [loading, setLoading] = useState(true);
  const [viewAsRole, setViewAsRoleState] = useState<AppRole | null>(() => {
    if (typeof window === "undefined") return null;
    const stored = sessionStorage.getItem(VIEW_AS_KEY);
    return stored && (VALID_ROLES as readonly string[]).includes(stored) ? (stored as AppRole) : null;
  });
  const mustUpdatePassword = !!user?.user_metadata?.must_update_password;

  const setViewAsRole = useCallback((r: AppRole | null) => {
    if (actualRole !== "admin") return;
    setViewAsRoleState(r);
    if (r) sessionStorage.setItem(VIEW_AS_KEY, r);
    else sessionStorage.removeItem(VIEW_AS_KEY);
  }, [actualRole]);

  // Only admins may override their role; drop any stale override otherwise.
  const activeViewAs = actualRole === "admin" ? viewAsRole : null;
  const effectiveRole = activeViewAs ?? actualRole;

  useEffect(() => {
    if (actualRole && actualRole !== "admin" && viewAsRole) {
      setViewAsRoleState(null);
      sessionStorage.removeItem(VIEW_AS_KEY);
    }
  }, [actualRole, viewAsRole]);

  // While previewing as an agent, don't let the admin's (missing) agent record
  // trigger onboarding/approval gates.
  const effectiveAgentStatus: AgentStatus | null =
    activeViewAs === "agent"
      ? { isApproved: true, onboardingComplete: true }
      : activeViewAs
        ? null
        : agentStatus;


  const fetchRole = useCallback(async (userId: string) => {
    const { data } = await supabase
      .from("user_roles")
      .select("role")
      .eq("user_id", userId);
    
    // Priority: admin > mls_admin > agent > client > co_buyer
    const rolePriority: AppRole[] = ["admin", "mls_admin", "agent", "client", "co_buyer"];
    const roles = (data || []).map(r => r.role as AppRole);
    const userRole = rolePriority.find(r => roles.includes(r)) || null;
    setActualRole(userRole);

    if (userRole === "agent") {
      const { data: agentProfile } = await supabase
        .from("agent_profiles")
        .select("is_approved")
        .eq("user_id", userId)
        .maybeSingle();

      setAgentStatus({
        isApproved: agentProfile?.is_approved ?? false,
        // Profile details (license, brokerage, bio) are optional — no onboarding gate
        onboardingComplete: true,
      });
    } else {
      setAgentStatus(null);
    }
  }, []);

  const refreshRole = useCallback(async () => {
    if (user) await fetchRole(user.id);
  }, [user, fetchRole]);

  useEffect(() => {
    // Set up auth state listener BEFORE getting session
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, newSession) => {
        setSession(newSession);
        setUser(newSession?.user ?? null);

        if (newSession?.user) {
          // Use setTimeout to avoid potential deadlocks with Supabase client
          setTimeout(() => fetchRole(newSession.user.id), 0);
        } else {
          setActualRole(null);
          setViewAsRoleState(null);
          sessionStorage.removeItem(VIEW_AS_KEY);
          setAgentStatus(null);
        }

        if (event === "SIGNED_OUT") {
          setActualRole(null);
          setViewAsRoleState(null);
          sessionStorage.removeItem(VIEW_AS_KEY);
          setAgentStatus(null);
        }
      }
    );

    // Then get the initial session
    supabase.auth.getSession().then(({ data: { session: initialSession } }) => {
      setSession(initialSession);
      setUser(initialSession?.user ?? null);
      if (initialSession?.user) {
        fetchRole(initialSession.user.id).finally(() => setLoading(false));
      } else {
        setLoading(false);
      }
    });

    return () => subscription.unsubscribe();
  }, [fetchRole]);

  const signOut = async () => {
    await supabase.auth.signOut();
    setUser(null);
    setSession(null);
    setActualRole(null);
    setViewAsRoleState(null);
    sessionStorage.removeItem(VIEW_AS_KEY);
    setAgentStatus(null);
  };

  return (
    <AuthContext.Provider value={{ user, session, role: effectiveRole, actualRole, agentStatus: effectiveAgentStatus, mustUpdatePassword, loading, viewAsRole: activeViewAs, setViewAsRole, signOut, refreshRole }}>
      {children}
    </AuthContext.Provider>
  );
};
